import { z } from "zod";

/**
 * One tool call the SDK refused to run during a turn. Single shared shape
 * across both wire hops:
 *
 *   1. Runner → host — `ResultSchema` (agent-ipc-v2/events/messages/
 *      result.event.ts) carries an array of these on the turn's Result.
 *   2. Host → browser — `MessageEndEventSchema` (chat-stream-events.types.ts)
 *      carries the same array on the turn's terminal event.
 *
 * Defined once because the two hops fail DIFFERENTLY on a field they were not
 * told about, and neither failure is loud. The runner hop is `.strict()` at the
 * union composition site, so an unknown key there refuses the WHOLE Result and
 * the turn's content, files and usage vanish with it. The browser hop is not
 * strict, so the same key is silently stripped and only the denial record is
 * lost. One schema is what removes the chance to miss either.
 *
 * FLOOR, NOT PROOF — a reader may conclude "at least these calls were denied"
 * and never "only these were". Measured against the shipped CLI, three
 * settings-rule denials produced only two entries in the source array: the
 * `Bash` and `Write` denials each landed, the `Read` denial produced an empty
 * array. A consumer auditing denials from this alone under-counts, and the path
 * it misses is the read path.
 *
 * Carries only what the SDK's own array carries. The SDK additionally declares
 * a richer per-denial system frame — with the deciding component's reason and
 * the subagent it fired in — and that frame does not arrive: three separate
 * denial provocations produced genuine denials and none emitted it. Those
 * fields are therefore absent rather than optional, because nothing can
 * populate them; the slice that wires the frame defensively adds them.
 *
 * The SDK's `tool_input` is deliberately not carried. The question this record
 * answers is which tools were blocked, not with what arguments, and the
 * arguments are an unbounded model-authored blob that would then cross both
 * hops and reach the browser.
 */
export const PermissionDenialSchema = z
  .object({
    toolName: z.string(),
    /** Joins the denial to the tool call it refused, where the transcript has one. */
    toolUseId: z.string()
  })
  .strict();

export type PermissionDenial = z.infer<typeof PermissionDenialSchema>;
